/**
 * Cameras Live — Ops Film Deck (redesign).
 * Soft≠Live: LIVE only with authoritative ticket; Soft demo never claims Live.
 *
 * Page Wall — mosaic lenses + film-rail dispatch
 * Page Evidence — full-page local IndexedDB cases (never a sidebar)
 * Honesty: AI following · Realtime stream · Owned · created · shared
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, LayoutGroup } from "framer-motion";
import { html, M, Ico, Alert, SOFT, go } from "./ui.js";
import { api, path, useApi, usePolled } from "./api.js";
import { CameraMiniPlayer } from "./camera-mini-player.js";
import { CameraSignalRail, buildCameraSignalFeed } from "./cameras-signal-rail.js";
import { cameraLabelSay, cameraPlantSay, looksLikeCode, operatorSay, boardHeadingOf } from "./operator-say.js";
import { useLive } from "./live.js";
import { useCameraSignalsLive } from "./stream-live.js";
import { closeLiveSession, openLiveSession } from "./udock-api.js";
import { LIVE_CONSOLE_CAP } from "./live-seat-logic.js";
import {
  captureFrameToLocal,
  captureFrameToServer,
  listLocalEvidence,
  retainMediaEvidenceLocal,
  saveAuditLocal,
  saveDispatchLocal,
} from "./cameras-local-vault.js";
import { mergeSoftCinemaDemo } from "./cameras-soft-demo.js";
import { cinemaCardLiveOf } from "./edgeos-live.js";
import { LocalEvidenceExport } from "./local-evidence-export.js";

const FOCUS_KEY = "croc.cam.focus";
const STAGGER = { type: "spring", stiffness: 420, damping: 32, mass: 0.55 };
const TAP = { scale: 0.96 };
const HOVER = { scale: 1.02, y: -2 };

function textOf(...values) {
  for (const value of values) {
    const text = typeof value === "string" ? value.trim() : "";
    if (text) return text;
  }
  return "";
}

function liveOf(card) {
  return cinemaCardLiveOf(card);
}

function sitOf(card) {
  return card && card.situation && typeof card.situation === "object" ? card.situation : {};
}

function deviceIdOf(card) {
  const binding = card && card.binding && typeof card.binding === "object" ? card.binding : {};
  return textOf(binding.device_id, card && card.device_id, card && card.subject_ref);
}

function cameraIdOf(card) {
  const binding = card && card.binding && typeof card.binding === "object" ? card.binding : {};
  return textOf(binding.camera_id, card && card.camera_id, card && card.source_id);
}

function scopeOf(card, deviceMap) {
  const sit = sitOf(card);
  const place = sit.place && typeof sit.place === "object" ? sit.place : {};
  const swarm = sit.swarm && typeof sit.swarm === "object" ? sit.swarm : {};
  const device = deviceIdOf(card);
  const liveDevice = (deviceMap && deviceMap.get(device)) || {};
  return {
    device,
    zone: textOf(place.zone_id, card && card.zone_id, liveDevice.zone),
    group: textOf(card && card.group_id, liveDevice.group, swarm.group_id),
  };
}

function readCameraHandoff() {
  try {
    const hash = String(location.hash || "");
    const query = hash.includes("?") ? hash.slice(hash.indexOf("?") + 1) : "";
    const params = new URLSearchParams(query);
    return {
      source: String(params.get("source") || ""),
      device: String(params.get("device") || ""),
      zone: String(params.get("zone") || ""),
      group: String(params.get("group") || ""),
      scope: String(params.get("scope") || ""),
    };
  } catch {
    return { source: "", device: "", zone: "", group: "", scope: "" };
  }
}

function scopeMatches(card, scope, deviceMap, cardsById) {
  if (!scope || scope.mode === "all" || !scope.id) return true;
  const linked = Array.isArray(card && card.linked_source_ids) ? card.linked_source_ids : [];
  const candidates = [card, ...linked.map((id) => cardsById[id]).filter(Boolean)];
  return candidates.some((candidate) => {
    const info = scopeOf(candidate, deviceMap);
    return String(info[scope.mode] || "") === String(scope.id);
  });
}

function scopeShareOf(card, deviceMap) {
  const device = (deviceMap && deviceMap.get(deviceIdOf(card))) || {};
  const soft = card && card.soft_share && typeof card.soft_share === "object" ? card.soft_share : null;
  const shared = !!(device.is_shared || card && card.is_shared || soft);
  return {
    shared,
    sharedBy: textOf(device.shared_by, card && card.shared_by, soft && soft.by),
  };
}

function upsertScopeOption(map, id, { label, shared, sharedBy }) {
  const key = String(id || "").trim();
  if (!key) return;
  const prev = map.get(key);
  if (prev && !prev.shared) return;
  if (prev && shared) return;
  map.set(key, {
    id: key,
    label: textOf(label, key),
    shared: !!shared,
    sharedBy: shared ? textOf(sharedBy) : "",
  });
}

function sortScopeOptions(map) {
  return [...map.values()].sort((a, b) => {
    if (!!a.shared !== !!b.shared) return a.shared ? 1 : -1;
    return String(a.label).localeCompare(String(b.label));
  });
}

/**
 * Scope lists only include admin-owned/created entries, plus cross-tenant
 * shares which always carry a bookmark (`shared: true`).
 */
function buildScopeOptions(cards, deviceMap, ownedZones) {
  const ownedZoneKeys = new Set();
  const ownedGroupKeys = new Set();
  for (const zone of ownedZones || []) {
    const id = textOf(zone && zone.id);
    const label = textOf(zone && zone.label);
    if (id) ownedZoneKeys.add(id);
    if (label) ownedZoneKeys.add(label);
  }
  const devices = new Map();
  const zones = new Map();
  const groups = new Map();

  for (const zone of ownedZones || []) {
    const id = textOf(zone && zone.id, zone && zone.label);
    if (!id) continue;
    upsertScopeOption(zones, id, {
      label: textOf(zone && zone.label, id),
      shared: false,
      sharedBy: "",
    });
  }

  for (const device of (deviceMap && deviceMap.values && deviceMap.values()) || []) {
    const zone = textOf(device && device.zone);
    const group = textOf(device && device.group);
    const shared = !!(device && device.is_shared);
    const sharedBy = textOf(device && device.shared_by);
    if (zone) {
      if (shared) {
        upsertScopeOption(zones, zone, { label: zone, shared: true, sharedBy });
      } else {
        ownedZoneKeys.add(zone);
        upsertScopeOption(zones, zone, { label: zone, shared: false, sharedBy: "" });
      }
    }
    if (group) {
      if (shared) {
        upsertScopeOption(groups, group, { label: group, shared: true, sharedBy });
      } else {
        ownedGroupKeys.add(group);
        upsertScopeOption(groups, group, { label: group, shared: false, sharedBy: "" });
      }
    }
  }

  for (const card of cards || []) {
    const info = scopeOf(card, deviceMap);
    const share = scopeShareOf(card, deviceMap);
    const knownDevice = !!(info.device && deviceMap && deviceMap.has(info.device));
    if (info.device) {
      upsertScopeOption(devices, info.device, {
        label: textOf(card && card.label, info.device),
        shared: share.shared,
        sharedBy: share.sharedBy,
      });
    }
    if (info.zone) {
      if (ownedZoneKeys.has(info.zone) || (knownDevice && !share.shared)) {
        ownedZoneKeys.add(info.zone);
        upsertScopeOption(zones, info.zone, {
          label: info.zone,
          shared: false,
          sharedBy: "",
        });
      } else if (share.shared) {
        upsertScopeOption(zones, info.zone, {
          label: info.zone,
          shared: true,
          sharedBy: share.sharedBy,
        });
      } else if ((card && (card.soft_demo || card.soft_stamp || card.soft_neq_live)) && !share.shared) {
        ownedZoneKeys.add(info.zone);
        upsertScopeOption(zones, info.zone, {
          label: info.zone,
          shared: false,
          sharedBy: "",
        });
      }
      // Foreign / unknown zones stay off the picker.
    }
    if (info.group) {
      if (ownedGroupKeys.has(info.group) || (knownDevice && !share.shared)) {
        ownedGroupKeys.add(info.group);
        upsertScopeOption(groups, info.group, {
          label: info.group,
          shared: false,
          sharedBy: "",
        });
      } else if (share.shared) {
        upsertScopeOption(groups, info.group, {
          label: info.group,
          shared: true,
          sharedBy: share.sharedBy,
        });
      } else if ((card && (card.soft_demo || card.soft_stamp || card.soft_neq_live)) && !share.shared) {
        // Soft own-fleet preview: treat as created, never as foreign share.
        ownedGroupKeys.add(info.group);
        upsertScopeOption(groups, info.group, {
          label: info.group,
          shared: false,
          sharedBy: "",
        });
      }
    }
  }

  return {
    device: sortScopeOptions(devices),
    zone: sortScopeOptions(zones),
    group: sortScopeOptions(groups),
  };
}

function controlTone(kind) {
  if (kind === "ai") return "ai";
  if (kind === "human") return "human";
  return "unknown";
}

function focusMap(card) {
  const sit = sitOf(card);
  const place = sit.place && typeof sit.place === "object" ? sit.place : {};
  const binding = card && card.binding && typeof card.binding === "object" ? card.binding : {};
  const device = textOf(binding.device_id, card && card.subject_ref);
  const payload = {
    source_id: textOf(card && card.source_id),
    device,
    zone: textOf(place.zone_id, card && card.zone_id),
    site: textOf(place.site_id, card && card.site_id),
    ll: Array.isArray(place.ll) ? place.ll : null,
    label: textOf(card && card.label),
    from: "camera_focus",
    handoff: true,
    ts: Date.now(),
  };
  try {
    sessionStorage.setItem(FOCUS_KEY, JSON.stringify(payload));
  } catch { /* */ }
  const q = new URLSearchParams();
  q.set("from", "camera_focus");
  q.set("handoff", "1");
  if (device) q.set("device", device);
  if (payload.zone) q.set("zone", payload.zone);
  go("/ops?" + q.toString());
}

function SoftPhosphor({ tone, stamp, quiet, kindMark, mediaKind }) {
  /* Honesty: SOFT · DEMO */
  return html`<div className="cw-soft" data-tone=${tone || "gate"} data-quiet=${quiet ? "1" : "0"}
      data-media=${mediaKind || ""} aria-hidden="true">
    <i className="cw-soft__grain"/>
    <i className="cw-soft__sweep"/>
    ${mediaKind === "recording" ? html`<i className="cw-soft__play"/>` : null}
    ${mediaKind === "upload" ? html`<i className="cw-soft__up"/>` : null}
    ${mediaKind === "snapshot" ? html`<i className="cw-soft__frame"/>` : null}
    ${kindMark ? html`<span className="cw-soft__kind" data-k=${kindMark}>${kindMark}</span>` : null}
    <span className="cw-soft__stamp">${stamp || "Preview"}</span>
  </div>`;
}

function dispatchLoopOf(sit) {
  if (sit && sit.dispatch_loop && typeof sit.dispatch_loop === "object") return sit.dispatch_loop;
  const d = sit && sit.dispatch && typeof sit.dispatch === "object" ? sit.dispatch : null;
  if (!d || !d.id) return null;
  return {
    id: d.id,
    state: d.state || "unknown",
    assignee: d.assignee || "",
    responder: d.assignee || "",
    eta_s: null,
    elapsed_s: null,
    camera_open: {
      personnel: { open: false, who: "", lens: "" },
      device: { open: false, device_id: "", lens: "" },
    },
    after_response: "",
    loop_result: "open",
    stages: [
      { id: "dispatch", label: "Dispatch", done: true },
      { id: "ack", label: "Ack", done: !!d.assignee },
      { id: "cam_person", label: "Person cam", done: false },
      { id: "cam_device", label: "Device cam", done: false },
      { id: "result", label: "Close", done: false },
    ],
  };
}

function loopIdOf(card) {
  const loop = dispatchLoopOf(sitOf(card));
  return loop && loop.id ? String(loop.id) : "";
}

/** One board card per dispatch loop — never twin a video card. */
function buildDispatchBoard(deviceCards, dispatchCards) {
  const byLoop = new Map();

  const absorb = (card, preferTitle) => {
    const loop = dispatchLoopOf(sitOf(card));
    if (!loop || !loop.id) return;
    const key = String(loop.id);
    const prev = byLoop.get(key);
    const linked = new Set((prev && prev.linked_lenses) || []);
    const linkedIds = new Set((prev && prev.linked_source_ids) || []);
    if (card.lane !== "dispatch") {
      linked.add(card.label || card.source_id);
      linkedIds.add(card.source_id);
    } else {
      linked.add(card.label || card.source_id);
      linkedIds.add(card.source_id);
    }
    byLoop.set(key, {
      source_id: "loop:" + key,
      label: preferTitle || (prev && prev.label) || loop.assignee || key,
      lane: "dispatch",
      soft_stamp: !!(card.soft_stamp || (prev && prev.soft_stamp)),
      soft_demo: !!(card.soft_demo || (prev && prev.soft_demo)),
      soft_neq_live: !!(card.soft_neq_live || (prev && prev.soft_neq_live)),
      situation: {
        ...(prev && prev.situation ? prev.situation : {}),
        ...sitOf(card),
        dispatch_loop: loop,
        why: sitOf(card).why || (prev && prev.situation && prev.situation.why) || ("Dispatch " + key),
      },
      linked_lenses: [...linked],
      linked_source_ids: [...linkedIds],
      loop_key: key,
    });
  };

  for (const c of dispatchCards || []) absorb(c, c.label);
  for (const c of deviceCards || []) absorb(c, null);
  return [...byLoop.values()];
}

function fmtDuration(seconds) {
  if (seconds == null || !Number.isFinite(Number(seconds))) return "—";
  const s = Math.max(0, Math.round(Number(seconds)));
  if (s < 60) return s + "s";
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? `${m}m ${r}s` : `${m}m`;
}

const THINK_LINES = [
  "Scoring FOV overlap…",
  "Ranking peer lenses…",
  "Preview only — live video is not claimed…",
  "Estimating dispatch ETA…",
  "Checking person / device cams…",
  "Next integrity stamp…",
];
/* Honesty: Soft≠Live — no Live claimed… */

function aiFollowOf(card, tick) {
  if (!card) {
    return { state: "idle", line: "Select a lens or loop.", tick: tick || 0 };
  }
  const sit = sitOf(card);
  const control = sit.control || {};
  const soft = !!(card.soft_stamp || card.soft_demo || card.soft_neq_live);
  const idx = Math.abs((tick || 0) + String(card.source_id).length) % THINK_LINES.length;
  return {
    state: control.kind === "human" ? "human_in_loop" : "ai_following",
    line: soft ? THINK_LINES[idx] : (sit.why || "Tracking selected lens."),
    tick: tick || 0,
  };
}

function aiMarkLabel(state) {
  if (state === "human_in_loop") return "Person";
  if (state === "ai_following") return "Watching";
  return "Waiting";
}

function AiWitnessMark({ state }) {
  const mode = state || "idle";
  const label = aiMarkLabel(mode);
  return html`<${M.div} className="cw-ai-mark" data-state=${mode} role="status"
      aria-label=${`AI following · ${label}`}
      initial=${{ opacity: 0, y: -8, scale: 0.92 }}
      animate=${{ opacity: 1, y: 0, scale: 1 }}
      transition=${{ duration: 0.42, ease: [.16, 1, .3, 1] }}>
    <span className="cw-ai-mark__field" aria-hidden="true">
      <i className="cw-ai-mark__ring" data-n="1"/>
      <i className="cw-ai-mark__ring" data-n="2"/>
      <i className="cw-ai-mark__ring" data-n="3"/>
      <i className="cw-ai-mark__sweep"/>
      <i className="cw-ai-mark__core"/>
    </span>
    <div className="cw-ai-mark__copy">
      <b>AI</b>
      <${AnimatePresence} mode="wait">
        <${M.em} key=${label} initial=${{ opacity: 0, y: 6, filter: "blur(4px)" }}
            animate=${{ opacity: 1, y: 0, filter: "blur(0px)" }}
            exit=${{ opacity: 0, y: -5, filter: "blur(3px)" }}
            transition=${{ duration: 0.28, ease: [.2, 1, .3, 1] }}>
          ${label}
        <//>
      <//>
    </div>
  <//>`;
}

function evidenceKind(row) {
  const explicit = String((row && (row.media_kind || row.kind)) || "").toLowerCase();
  if (explicit === "upload" || explicit === "recording" || explicit === "snapshot"
    || explicit === "dispatch" || explicit === "audit") {
    return explicit;
  }
  const origin = String((row && row.trigger_origin) || "").toLowerCase();
  if (origin.includes("upload") || origin.includes("personnel")) return "upload";
  if (origin.includes("record") || origin.includes("clip")) return "recording";
  if (origin.includes("dispatch") || origin === "local_dispatch") return "dispatch";
  return "snapshot";
}

function evidenceLabel(kind) {
  if (kind === "upload") return "Upload";
  if (kind === "recording") return "Recording";
  if (kind === "dispatch") return "Dispatch";
  if (kind === "audit") return "Audit";
  return "Snapshot";
}

function evidenceImageUrl(row) {
  const direct = textOf(row && row.image_url, row && row.thumb_url);
  if (direct) return direct;
  const id = textOf(row && row.snapshot_id);
  if (!id || !row || row.has_image !== true) return "";
  try {
    return path("get__ops_cameras_frames_p_image", { pathParams: [id] });
  } catch {
    return "";
  }
}

/** Collect snap / clip / upload / dispatch scoped to ONE event archive only. */
function collectEvidenceForEvent(seed, cardsById, localRows) {
  const out = [];
  const seen = new Set();
  const push = (row, from) => {
    if (!row) return;
    const kind = evidenceKind(row);
    const id = String(row.snapshot_id || row.id || "") + ":" + kind;
    if (!id || id === ":" + kind || seen.has(id)) return;
    seen.add(id);
    out.push({
      ...row,
      media_kind: kind,
      image_url: evidenceImageUrl(row),
      from_label: from || row.from_label || "",
      retained_local: !!(row.retained_local || row.trigger_origin === "local_vault" || row.blob),
      event_id: row.event_id || (seed && seed.id) || "",
      note: textOf(row.note, row.reason),
      author: textOf(row.author, row.who),
      author_role: textOf(row.author_role, row.role),
      ai_read: textOf(row.ai_read, row.ai_summary),
      ai_fixed: !!(row.ai_fixed || row.fixed || row.sealed),
    });
  };

  if (!seed || !seed.id) return out;

  const sourceIds = seed.source_ids instanceof Set
    ? seed.source_ids
    : new Set(seed.source_ids || []);
  for (const sid of sourceIds) {
    const card = cardsById && cardsById[sid];
    if (!card) continue;
    for (const row of (Array.isArray(sitOf(card).evidence) ? sitOf(card).evidence : [])) {
      push(row, card.label || sid);
    }
  }

  const loop = seed.loop;
  if (loop && loop.id) {
    const hasLocalDisp = (localRows || []).some((r) => String(r.id || "") === `disp_${loop.id}`);
    if (!hasLocalDisp) {
      push({
        snapshot_id: "live_disp_" + loop.id,
        integrity_mark: `DSP-${String(loop.id).slice(0, 10)}`,
        trigger_kind: "dispatch",
        trigger_origin: "dispatch_loop",
        media_kind: "dispatch",
        soft_frame_tone: "field",
        detail: [
          loop.assignee || "—",
          loop.state || "open",
          loop.after_response || "",
        ].filter(Boolean).join(" · "),
        at: Date.now(),
        loop,
        event_id: seed.id,
      }, "Loop");
    }
  }

  for (const row of (localRows || [])) {
    const rowEvent = localEventIdOf(row);
    if (rowEvent !== seed.id) continue;
    const kind = evidenceKind(row);
    push({
      snapshot_id: row.id || row.integrity_mark,
      integrity_mark: row.integrity_mark || "LOCAL",
      trigger_kind: row.trigger_kind || "human",
      trigger_origin: "local_vault",
      media_kind: kind,
      soft_frame_tone: row.soft_frame_tone || (kind === "dispatch" ? "field" : "dock"),
      detail: row.detail || "",
      note: row.note || "",
      author: row.author || row.label || "",
      author_role: row.author_role || "",
      ai_read: row.ai_read || "",
      ai_fixed: !!(row.ai_fixed || row.fixed),
      blob: row.blob || null,
      mime: row.mime || "",
      retained_local: true,
      at: row.at || row.created_at,
      label: row.label || "",
      event_id: seed.id,
      loop: kind === "dispatch" ? (seed.loop || null) : undefined,
    }, "Local vault");
  }

  return out.sort((a, b) => Number(b.at || 0) - Number(a.at || 0));
}

/** One occurrence key: dispatch loop id, else trig:<source>. Never merge events. */
function eventIdOf(card) {
  if (!card) return "";
  const loop = dispatchLoopOf(sitOf(card));
  if (loop && loop.id) return String(loop.id);
  if (card.source_id) return `trig:${card.source_id}`;
  return "";
}

function localEventIdOf(row) {
  if (!row) return "";
  const eid = String(row.event_id || row.loop_id || "").trim();
  if (eid) return eid;
  const id = String(row.id || "");
  if (id.startsWith("disp_")) return id.slice(5);
  if (row.source_id) return `trig:${row.source_id}`;
  return id ? `loc:${id}` : "";
}

function msOf(value) {
  if (value == null || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Catalog: each distinct event / trigger = one archive seed. */
function buildEventCatalog(cardsById, localRows) {
  const map = new Map();

  const touch = (id, patch = {}) => {
    const key = String(id || "").trim();
    if (!key) return null;
    let cur = map.get(key);
    if (!cur) {
      cur = {
        id: key,
        source_ids: new Set(),
        loop: null,
        card: null,
        label: "",
        why: "",
        place: {},
        soft: false,
        at: 0,
        media_hint: 0,
      };
      map.set(key, cur);
    }
    if (patch.loop && (!cur.loop || (patch.loop.opened_at && !cur.loop.opened_at))) {
      cur.loop = patch.loop;
    }
    if (patch.card && (!cur.card || patch.preferCard)) cur.card = patch.card;
    if (patch.label && !cur.label) cur.label = patch.label;
    if (patch.why && !cur.why) cur.why = patch.why;
    if (patch.place && Object.keys(patch.place).length && !Object.keys(cur.place || {}).length) {
      cur.place = patch.place;
    }
    if (patch.soft) cur.soft = true;
    if (patch.source_id) cur.source_ids.add(patch.source_id);
    if (patch.at && patch.at > cur.at) cur.at = patch.at;
    if (patch.media_hint) cur.media_hint += patch.media_hint;
    return cur;
  };

  for (const card of Object.values(cardsById || {})) {
    if (!card || !card.source_id) continue;
    const eid = eventIdOf(card);
    const sit = sitOf(card);
    const loop = dispatchLoopOf(sit);
    const place = (sit.place && typeof sit.place === "object") ? sit.place : {};
    const evidenceN = Array.isArray(sit.evidence) ? sit.evidence.length : 0;
    touch(eid, {
      card,
      preferCard: !!(loop && loop.id),
      loop,
      label: (loop && (loop.assignee || loop.id)) || card.label || eid,
      why: sit.why || "",
      place,
      soft: !!(card.soft_stamp || card.soft_demo || card.soft_neq_live),
      source_id: card.source_id,
      at: msOf(loop && loop.opened_at) || (Number(card.pulse_at || 0) * 1000) || Date.now(),
      media_hint: evidenceN,
    });
  }

  for (const row of (localRows || [])) {
    const eid = localEventIdOf(row);
    if (!eid) continue;
    touch(eid, {
      label: row.label || eid,
      source_id: row.source_id || "",
      at: Number(row.at || 0) || Date.now(),
      soft: true,
      media_hint: evidenceKind(row) === "dispatch" ? 0 : 1,
    });
  }

  return [...map.values()]
    .map((seed) => {
      const evidence = collectEvidenceForEvent(seed, cardsById, localRows);
      const archive = buildEventArchive(seed.card, cardsById, evidence, seed.loop);
      const mediaN = evidence.filter((r) => r.media_kind !== "dispatch" && r.media_kind !== "audit").length;
      return {
        ...seed,
        source_ids: [...seed.source_ids],
        evidence,
        archive,
        media_count: mediaN,
        at: seed.at || msOf(seed.loop && seed.loop.opened_at) || 0,
      };
    })
    .sort((a, b) => Number(b.at || 0) - Number(a.at || 0));
}

function sessionIdOf(card) {
  const session = card && card.session && typeof card.session === "object" ? card.session : {};
  return textOf(session.session_id, card && card.session_id);
}

function lensNameOf(card, index) {
  const binding = (card && card.binding && typeof card.binding === "object") ? card.binding : {};
  return cameraPlantSay({
    label: card && card.label,
    brand: card && card.brand,
    protocol: card && card.protocol,
    vendor: card && card.vendor,
    camera_id: binding.camera_id,
    source_id: card && card.source_id,
  }, index);
}

function VideoCard({ card, selected, related, alarmLinked, whepUrl, ticketReason, index, onSelect, onSaveFrame, onToggleLive, liveBusy, liveOk, onOpenEvidence, toast }) {
  const sit = sitOf(card);
  const control = sit.control || {};
  const soft = !!(card.soft_stamp || card.soft_demo || card.soft_neq_live);
  const liveCapable = liveOf(card);
  const sessionId = sessionIdOf(card);
  const deviceId = deviceIdOf(card);
  /* Product Live Soft≠ Soft Cover: WHEP preferred · remux AU when ticket dark / cap. */
  const live = !!(liveCapable && (whepUrl || (sessionId && deviceId)));
  const videoHost = useRef(null);
  const loopId = loopIdOf(card);

  /* Open for live AI + dispatch */
  return html`<${M.article}
      layout
      className="cw-vcard"
      data-loop=${loopId || ""}
      data-selected=${selected ? "1" : "0"}
      data-related=${related && !selected ? "1" : "0"}
      data-alarm-linked=${alarmLinked && !selected ? "1" : "0"}
      data-soft=${soft ? "1" : "0"}
      data-live=${live ? "1" : "0"}
      initial=${{ opacity: 0, y: 12 }}
      animate=${{ opacity: 1, y: 0 }}
      whileHover=${{ y: -4, scale: 1.015 }}
      whileTap=${TAP}
      exit=${{ opacity: 0, scale: 0.98 }}
      transition=${{ ...STAGGER, delay: Math.min(index * 0.04, 0.2) }}
      onClick=${() => onSelect(card.source_id)}>
    <div className="cw-vcard__stage" ref=${videoHost}>
      ${soft || !live
        ? html`<${SoftPhosphor} tone=${card.soft_preview_tone || "gate"} quiet=${true}/>`
        : html`<${CameraMiniPlayer}
            label=${card.label} kind=${card.source_kind || card.lane} live=${live}
            reason=${ticketReason || sit.why || "Not live"} whepUrl=${whepUrl || ""}
            sessionId=${sessionId} deviceId=${deviceId}/>`}
      <div className="cw-vcard__badges">
        <b data-k=${live ? "live" : "soft"}>${live ? "LIVE" : "Preview"}</b>
        ${card.recording && card.recording.active
          ? html`<b data-k="recording">REC</b>`
          : null}
        <b data-k=${controlTone(control.kind)}>${control.kind === "ai" ? "AI" : (control.kind === "human" ? "Person" : "—")}</b>
      </div>
    </div>
    <div className="cw-vcard__meta">
      <h3>${lensNameOf(card, index)}</h3>
      <p className="cw-vcard__why">${operatorSay(textOf(sit.why, sit.selection), "Ready to watch")}</p>
      <footer>
        ${!soft && deviceId ? html`<${M.button} type="button"
          className="btn btn--ghost btn--xs"
          data-cmc-cam-live-ok=${liveOk ? "1" : "0"}
          disabled=${liveBusy || !liveOk}
          aria-busy=${liveBusy ? "true" : "false"}
          whileHover=${{ scale: 1.06 }} whileTap=${TAP} transition=${SOFT}
          onClick=${async (ev) => {
            try { ev.stopPropagation(); } catch { /* */ }
            await onToggleLive(card);
          }}>${liveBusy ? "Working…" : (sessionId ? "Stop Live" : "Start Live")}<//>` : null}
        <${M.button} type="button" className="btn btn--ghost btn--xs"
          whileHover=${{ scale: 1.06 }} whileTap=${TAP} transition=${SOFT}
          onClick=${(ev) => { try { ev.stopPropagation(); } catch { /* */ } focusMap(card); }}>Map<//>
        <${M.button} type="button" className="btn btn--ghost btn--xs"
          data-cmc-cam-capture-ok=${liveOk ? "1" : "0"}
          whileHover=${{ scale: 1.06 }} whileTap=${TAP} transition=${SOFT}
          onClick=${async (ev) => {
            try { ev.stopPropagation(); } catch { /* */ }
            try {
              const video = videoHost.current && videoHost.current.querySelector("video");
              await onSaveFrame(card, video);
              onOpenEvidence && onOpenEvidence();
            } catch (err) {
              toast && toast(String((err && err.message) || err || "Save failed"), "warn");
            }
          }}>Evidence<//>
      </footer>
    </div>
  <//>`;
}

function DispatchCard({ card, selected, related, index, onSelect }) {
  const sit = sitOf(card);
  const loop = dispatchLoopOf(sit) || {};
  const soft = !!(card.soft_stamp || card.soft_demo || card.soft_neq_live);
  const lenses = card.linked_lenses || [];
  const title = loop.assignee
    || (lenses[0] ? String(lenses[0]).split("·")[0].trim() : "")
    || card.label
    || loop.id
    || "Loop";
  const reason = textOf(loop.dispatch_reason, sit.dispatch && sit.dispatch.summary, sit.why);

  return html`<${M.article}
      layout
      className="cw-dcard"
      data-selected=${selected ? "1" : "0"}
      data-related=${related && !selected ? "1" : "0"}
      data-soft=${soft ? "1" : "0"}
      data-state=${loop.state || "open"}
      initial=${{ opacity: 0, y: 8 }}
      animate=${{ opacity: 1, y: 0 }}
      whileHover=${{ y: -3, scale: 1.02 }}
      whileTap=${TAP}
      transition=${{ ...STAGGER, delay: Math.min(index * 0.04, 0.16) }}
      onClick=${() => onSelect(card.source_id)}>
    <header>
      <small>${loop.id || card.loop_key || "dispatch"}</small>
      <em>${loop.state || "open"}</em>
    </header>
    <h3>${title}</h3>
    <p className="cw-dcard__story">
      <span>${loop.responder || loop.assignee || "—"}</span>
      <span>ETA ${fmtDuration(loop.elapsed_s)}/${fmtDuration(loop.eta_s)}</span>
      ${lenses[0] ? html`<span>${lenses[0]}</span>` : null}
    </p>
    ${reason ? html`<p className="cw-dcard__reason">${reason}</p>` : null}
  <//>`;
}

function LiveLensSheet({
  card,
  signals,
  follow,
  whepUrl,
  ticketReason,
  onClose,
  onOpenEvidence,
  onFocusMap,
}) {
  if (!card) return null;
  const sit = sitOf(card);
  const loop = dispatchLoopOf(sit);
  const control = sit.control || {};
  const soft = !!(card.soft_stamp || card.soft_demo || card.soft_neq_live);
  const liveCapable = liveOf(card);
  const sessionId = sessionIdOf(card);
  const deviceId = deviceIdOf(card);
  const live = !!(liveCapable && (whepUrl || (sessionId && deviceId)));
  const rows = Array.isArray(signals) ? signals : [];
  const evidence = Array.isArray(sit.evidence) ? sit.evidence : [];
  const actions = Array.isArray(sit.ai_actions) ? sit.ai_actions : [];
  const reason = textOf(
    loop && loop.dispatch_reason,
    sit.dispatch && sit.dispatch.summary,
    sit.why,
  );
  const triggers = [];
  const pushTrig = (k, v) => {
    const val = textOf(v);
    if (!val) return;
    if (triggers.some((t) => t.v === val)) return;
    triggers.push({ k, v: val });
  };
  pushTrig("Why", sit.why);
  pushTrig("Dispatch", reason);
  pushTrig("Selection", sit.selection);
  pushTrig("Ticket", ticketReason);
  pushTrig("Following", follow && follow.line);
  pushTrig("Alarm", sit.alarm && (sit.alarm.summary || sit.alarm.kind));
  if (!triggers.length) {
    pushTrig("Status", live ? "Live lens open — waiting for a story of what opened it." : (soft ? "Preview only — not live video." : "No story of what opened this lens yet."));
  }

  return html`<${M.div} className="cw-lens-sheet" role="dialog" aria-modal="true"
      aria-label=${`Live detail · ${card.label || card.source_id}`}
      initial=${{ opacity: 0 }} animate=${{ opacity: 1 }} exit=${{ opacity: 0 }}
      transition=${{ duration: 0.2 }}>
    <button type="button" className="cw-lens-sheet__scrim" aria-label="Close" onClick=${onClose}/>
    <${M.div} className="cw-lens-sheet__panel"
        initial=${{ opacity: 0, y: 28, scale: 0.98 }}
        animate=${{ opacity: 1, y: 0, scale: 1 }}
        exit=${{ opacity: 0, y: 18, scale: 0.98 }}
        transition=${{ duration: 0.28, ease: [.16, 1, .3, 1] }}>
      <header className="cw-lens-sheet__head">
        <div>
          <small>${live ? "Live video" : (soft ? "Soft preview" : "Lens detail")}</small>
          <h2>${lensNameOf(card, 0)}</h2>
        </div>
        <${M.button} type="button" className="btn btn--ghost btn--xs cw-top__action"
          whileHover=${{ scale: 1.04 }} whileTap=${TAP} transition=${SOFT} onClick=${onClose}>
          Close
        <//>
      </header>

      <div className="cw-lens-sheet__body">
        <div className="cw-lens-sheet__hero">
          <div className="cw-lens-sheet__stage" data-live=${live ? "1" : "0"}>
            ${live
              ? html`<${CameraMiniPlayer}
                  label=${card.label || card.source_id}
                  kind=${card.source_kind || card.lane || "device"}
                  live=${true}
                  reason=${ticketReason || sit.why || "Live"}
                  whepUrl=${whepUrl || ""}
                  sessionId=${sessionId}
                  deviceId=${deviceId}
                />`
              : html`<${SoftPhosphor}
                  tone=${card.soft_preview_tone || "gate"}
                  stamp=${soft ? "Preview" : (ticketReason || "Not live yet")}
                  quiet=${false}
                  mediaKind=${"recording"}
                />`}
            <div className="cw-lens-sheet__stage-meta">
              <b>${live ? "Live picture" : "Preview"}</b>
              <span>${live ? "Ready to watch" : (soft ? "Preview only — not live video." : "Waiting to go live")}</span>
            </div>
          </div>
        </div>

        <aside className="cw-lens-sheet__rail" aria-label="Live execution process">
          <header>
            <span>Live process</span>
            <em>${control.kind === "ai" ? "AI in loop" : (control.kind === "human" ? "Human in loop" : "Watching")}</em>
          </header>
          <section>
            <h3>AI steps</h3>
            ${actions.length ? html`<ul className="cw-lens-sheet__rail-list">
              ${actions.slice().reverse().map((act) => html`<li key=${act.id || act.at}>
                <b>${textOf(act.action, act.kind)}</b>
                <span>${textOf(act.detail, act.summary)}</span>
              </li>`)}
            </ul>` : html`<p className="cw-lens-sheet__muted">No AI steps yet.</p>`}
          </section>
          <section>
            <h3>Dispatch / AI tape</h3>
            <div className="cw-lens-sheet__tape">
              ${rows.length ? rows.map((row) => html`<div key=${row.id} className="cw-signal__line" data-kind=${row.kind}>
                <em>${row.kind}</em>
                <p><b>${row.kind === "lens" ? cameraLabelSay(row.title) : row.title}</b><span>${row.detail}</span></p>
              </div>`) : html`<p className="cw-lens-sheet__muted">No live rows yet.</p>`}
            </div>
          </section>
        </aside>
      </div>

      <div className="cw-lens-sheet__below">
        <div className="cw-lens-sheet__highlight" data-live=${live ? "1" : "0"}>
          <div className="cw-lens-sheet__chips">
            <em data-k=${live ? "live" : "soft"}>${live ? "LIVE" : "Preview"}</em>
            <em data-k=${controlTone(control.kind)}>${control.kind === "ai" ? "AI" : (control.kind === "human" ? "HUMAN" : "—")}</em>
            ${loop ? html`<em data-k="state">${loop.state || "open"}</em>` : null}
          </div>
          <p className="cw-lens-sheet__lead">
            ${reason || (follow && follow.line) || sit.why || "Watching this lens."}
          </p>
        </div>

        <div className="cw-lens-sheet__scroll">
          <section className="cw-lens-sheet__block" data-hi="1">
            <h3>Why · what triggered</h3>
            <ul className="cw-lens-sheet__triggers">
              ${triggers.map((t) => html`<li key=${t.k + t.v}>
                <abbr>${t.k}</abbr>
                <span>${t.v}</span>
              </li>`)}
            </ul>
          </section>

          <section className="cw-lens-sheet__block">
            <h3>Field media on this event</h3>
            <p className="cw-lens-sheet__hint">
              Responder video / photo may include a written reason. AI reads that reason, then may seal it into Evidence when the event closes.
            </p>
            ${evidence.length ? html`<ul>
              ${evidence.map((row) => html`<li key=${row.snapshot_id}>
                <b>${evidenceLabel(row.media_kind)}</b>
                ${row.note ? html`<span>Reason: ${row.note}</span>` : null}
                ${row.ai_read ? html`<span>AI: ${row.ai_read}</span>` : null}
                ${row.ai_fixed ? html`<em>fixed</em>` : null}
              </li>`)}
            </ul>` : html`<p className="cw-lens-sheet__muted">No media on this live card yet.</p>`}
          </section>
        </div>
      </div>

      <footer className="cw-lens-sheet__foot">
        <${M.button} type="button" className="btn btn--ghost btn--xs cw-top__action cw-top__action--refresh"
          whileHover=${{ scale: 1.04 }} whileTap=${TAP} transition=${SOFT}
          onClick=${() => onFocusMap && onFocusMap(card)}>
          Focus on map
        <//>
        <${M.button} type="button" className="btn btn--ghost btn--xs cw-top__action"
          whileHover=${{ scale: 1.04 }} whileTap=${TAP} transition=${SOFT}
          onClick=${() => { onClose && onClose(); onOpenEvidence && onOpenEvidence(); }}>
          Open Evidence
        <//>
      </footer>
    <//>
  <//>`;
}

function ScopeSelector({ scope, setScope, options, selectedScope }) {
  const modes = ["all", "device", "zone", "group"];
  const chooseMode = (mode) => {
    if (mode === "all") {
      setScope({ mode: "all", id: "" });
      return;
    }
    const preferred = selectedScope && selectedScope[mode];
    const list = options[mode] || [];
    const ids = list.map((row) => String(row && row.id != null ? row.id : row));
    setScope({ mode, id: (preferred && ids.includes(preferred) ? preferred : ids[0]) || "" });
  };
  const values = scope.mode === "all" ? [] : (options[scope.mode] || []);
  return html`<div className="cw-scope" data-expanded=${scope.mode === "all" ? "0" : "1"} aria-label="Camera scope">
    <div className="cw-scope__bar">
      <div className="cw-scope__modes" role="tablist" aria-label="Live scope type">
        ${modes.map((mode) => {
          const unavailable = mode !== "all" && !(options[mode] || []).length;
          return html`<${M.button} type="button" role="tab"
              key=${mode} data-on=${scope.mode === mode ? "1" : "0"}
              disabled=${unavailable}
              aria-selected=${scope.mode === mode ? "true" : "false"}
              whileHover=${unavailable ? undefined : { y: -1 }} whileTap=${unavailable ? undefined : TAP}
              transition=${SOFT} onClick=${() => chooseMode(mode)}>
            ${mode === "all" ? "All" : mode === "device" ? "Board" : mode === "zone" ? "Area" : mode === "group" ? "Group" : mode}
          <//>`;
        })}
      </div>
      <p>${scope.mode === "all" ? "Yours, made here, or shared" : `${values.length} ${scope.mode === "device" ? "board" : scope.mode === "zone" ? "area" : scope.mode}${values.length === 1 ? "" : "s"}`}</p>
    </div>
    ${scope.mode !== "all" ? html`<${M.div} className="cw-scope__list" role="listbox"
        aria-label=${`Choose ${scope.mode}`} initial=${{ opacity: 0, y: -5 }}
        animate=${{ opacity: 1, y: 0 }} transition=${SOFT}>
      ${values.map((row) => {
        const id = String(row && row.id != null ? row.id : row);
        const label = looksLikeCode(id) ? textOf(row && row.label, "This place") : textOf(row && row.label, id);
        const shared = !!(row && row.shared);
        const sharedBy = textOf(row && row.sharedBy);
        return html`<${M.button} type="button" role="option" key=${id}
            data-on=${scope.id === id ? "1" : "0"}
            data-shared=${shared ? "1" : "0"}
            aria-selected=${scope.id === id ? "true" : "false"}
            title=${shared ? `Shared by ${sharedBy || "partner tenant"}` : label}
            whileHover=${{ y: -1 }} whileTap=${TAP} transition=${SOFT}
            onClick=${() => setScope({ mode: scope.mode, id })}>
          <i aria-hidden="true"/>
          <span>${label}</span>
          ${shared ? html`<abbr className="cw-scope__bookmark" title=${`Cross-tenant share · ${sharedBy || "partner"}`}>Bookmark</abbr>` : null}
          ${scope.id === id ? html`<b>Viewing</b>` : null}
        <//>`;
      })}
    <//>` : null}
  </div>`;
}

function BlobImg({ blob, alt }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!blob) { setUrl(""); return undefined; }
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => { try { URL.revokeObjectURL(next); } catch { /* */ } };
  }, [blob]);
  if (!url) return null;
  return html`<img className="cw-vault__blob" src=${url} alt=${alt || "local evidence"}/>`;
}

function EvidenceHero({ active }) {
  if (!active) {
    return html`<div className="cw-vault__empty">Empty</div>`;
  }
  if (active.media_kind === "dispatch") {
    const lp = active.loop || {};
    return html`<div className="cw-vault__disp-card">
      <small>Dispatch</small>
      <strong>${active.label || lp.assignee || active.integrity_mark || "Dispatch"}</strong>
      <p>${active.detail || "Retained locally."}</p>
    </div>`;
  }
  if (active.blob) {
    return html`<${BlobImg} blob=${active.blob} alt=${active.integrity_mark || "local"}/>`;
  }
  if (active.image_url) {
    return html`<img className="cw-vault__blob" src=${active.image_url}
      alt=${active.integrity_mark || "retained evidence"}/>`;
  }
  return html`<${SoftPhosphor}
      tone=${active.soft_frame_tone || "gate"}
      stamp=${(active.integrity_mark && !looksLikeCode(active.integrity_mark)) ? active.integrity_mark : "Preview"}
      kindMark=${evidenceLabel(active.media_kind)}
      mediaKind=${active.media_kind === "audit" ? "snapshot" : active.media_kind}
      quiet=${false}/>`;
}

function fmtClock(isoOrMs) {
  if (isoOrMs == null || isoOrMs === "") return "—";
  const ms = typeof isoOrMs === "number" ? isoOrMs : Date.parse(String(isoOrMs));
  if (!Number.isFinite(ms)) return "—";
  try {
    return new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    return "—";
  }
}

/** One occurrence = one complete archive: people · route · timeline · media · closure. */
function buildEventArchive(card, cardsById, evidence, loop) {
  const sit = card ? sitOf(card) : {};
  const place = (sit.place && typeof sit.place === "object") ? sit.place : {};
  const loopKey = (loop && loop.id) || (card ? eventIdOf(card) : "") || "";
  const linked = new Set();
  if (card && card.source_id) linked.add(card.source_id);
  for (const id of (card && card.linked_source_ids) || []) linked.add(id);
  if (loop && loop.id && cardsById) {
    for (const other of Object.values(cardsById)) {
      if (!other) continue;
      if (eventIdOf(other) === loopKey) {
        linked.add(other.source_id);
        for (const id of other.linked_source_ids || []) linked.add(id);
      }
    }
  }

  const timeline = [];
  const pushNode = (node) => {
    if (!node || !node.id) return;
    timeline.push(node);
  };

  if (loop && loop.opened_at) {
    pushNode({
      id: "evt-open",
      kind: "start",
      title: "Opened",
      detail: `Loop ${loop.id}`,
      at: loop.opened_at,
    });
  } else if (card && !loop) {
    pushNode({
      id: "evt-open",
      kind: "start",
      title: "Triggered",
      detail: sit.why || (card.label || card.source_id),
      at: evidence && evidence.length ? evidence[evidence.length - 1].at : Date.now(),
    });
  }

  for (const id of linked) {
    const other = (cardsById && cardsById[id]) || (card && card.source_id === id ? card : null);
    if (!other) continue;
    const actions = sitOf(other).ai_actions;
    if (!Array.isArray(actions)) continue;
    for (const act of actions) {
      pushNode({
        id: `act-${act.id || act.verb}-${act.at || ""}`,
        kind: "action",
        title: String(act.verb || "action"),
        detail: String(act.detail || ""),
        result: act.result || "",
        at: act.at || null,
        from: other.label || id,
      });
    }
  }

  for (const st of (loop && loop.stages) || []) {
    if (!st || !st.done) continue;
    pushNode({
      id: `stage-${st.id}`,
      kind: "stage",
      title: st.label || st.id,
      detail: "Done",
      at: loop.responded_at || loop.opened_at || null,
    });
  }

  for (const row of evidence || []) {
    if (!row || row.media_kind === "dispatch" || row.media_kind === "audit") continue;
    pushNode({
      id: `media-${row.snapshot_id}`,
      kind: "media",
      title: evidenceLabel(row.media_kind) + (row.ai_fixed ? " · fixed" : ""),
      detail: [
        row.integrity_mark || row.snapshot_id,
        row.author || row.from_label || "",
        row.note || "",
      ].filter(Boolean).join(" · "),
      media_kind: row.media_kind,
      snapshot_id: row.snapshot_id,
      at: row.at || null,
    });
  }

  if (loop && (loop.loop_result === "closed" || loop.loop_result === "improving")) {
    pushNode({
      id: "evt-close",
      kind: "close",
      title: loop.loop_result === "closed" ? "Closed" : "Improving",
      detail: loop.after_response || "Updated",
      at: Date.now(),
    });
  }

  timeline.sort((a, b) => {
    const am = a.at == null ? 0 : (typeof a.at === "number" ? a.at : Date.parse(String(a.at)) || 0);
    const bm = b.at == null ? 0 : (typeof b.at === "number" ? b.at : Date.parse(String(b.at)) || 0);
    return am - bm;
  });

  const route = Array.isArray(loop && loop.route) ? loop.route : [];
  const media = (evidence || []).filter((r) => r && r.media_kind !== "dispatch" && r.media_kind !== "audit");
  const stages = (loop && loop.stages) || [];
  const doneN = stages.filter((s) => s.done).length;
  const progress = stages.length ? Math.round((doneN / stages.length) * 100) : 0;
  const camP = (loop && loop.camera_open && loop.camera_open.personnel) || {};
  const camD = (loop && loop.camera_open && loop.camera_open.device) || {};

  const notes = [];
  const reason = textOf(loop && loop.dispatch_reason, sit.why);
  if (reason) {
    notes.push({
      id: "note-dispatch",
      who: (loop && (loop.assignee || loop.responder)) || "Dispatch",
      role: "dispatch",
      text: reason,
      at: loop && loop.opened_at,
    });
  }
  for (const row of media) {
    if (!row || !textOf(row.note)) continue;
    if (row.note === reason) continue;
    notes.push({
      id: `note-${row.snapshot_id}`,
      who: row.author || row.from_label || "Field",
      role: row.author_role || (row.media_kind === "upload" ? "responder" : "operator"),
      text: row.note,
      media_id: row.snapshot_id,
      at: row.at,
    });
  }

  const aiReads = media
    .filter((row) => row && (row.ai_read || row.ai_fixed))
    .map((row) => ({
      id: row.snapshot_id,
      mark: row.integrity_mark || row.snapshot_id,
      kind: row.media_kind,
      read: row.ai_read || "Understood",
      fixed: !!row.ai_fixed,
    }));
  const fixedCount = aiReads.filter((r) => r.fixed).length;

  return {
    id: loopKey || (card && card.source_id) || "",
    label: (card && card.label) || (loop && (loop.assignee || loop.id)) || "Case",
    why: sit.why || "",
    place,
    loop: loop || null,
    people: {
      assignee: (loop && loop.assignee) || "",
      responder: (loop && loop.responder) || "",
    },
    reason,
    notes,
    ai_reads: aiReads,
    fixed_count: fixedCount,
    route,
    stages,
    progress,
    timeline,
    media,
    cameras: { personnel: camP, device: camD },
    closure: {
      result: (loop && loop.loop_result) || "open",
      after: (loop && loop.after_response) || "",
      state: (loop && loop.state) || "open",
    },
    linked_count: linked.size,
  };
}

function EventDossier({ archive, activeId, onPickMedia }) {
  if (!archive || !archive.id) {
    return html`<aside className="cw-dossier" aria-label="Case">
      <p className="cw-dossier__hint">Open a case card.</p>
    </aside>`;
  }
  const {
    loop, people, place, route, stages, progress, timeline, media, cameras, closure,
    notes, ai_reads: aiReads, fixed_count: fixedCount, reason,
  } = archive;
  const camP = (cameras && cameras.personnel) || {};
  const camD = (cameras && cameras.device) || {};
  const lp = loop || {};
  const noteRows = Array.isArray(notes) ? notes : [];
  const reads = Array.isArray(aiReads) ? aiReads : [];

  return html`<aside className="cw-dossier" aria-label="Case dossier">
    <header className="cw-dossier__head">
      <div>
        <small>Case brief</small>
        <h3>${archive.label || "Evidence case"}</h3>
      </div>
      <p>
        <em data-k=${closure.state}>${closure.state}</em>
        <span>${progress}%</span>
        <b data-k=${closure.result}>${closure.result}</b>
        ${fixedCount ? html`<b data-k="fixed">${fixedCount} fixed</b>` : null}
      </p>
    </header>

    <section className="cw-dossier__block">
      <h4>Dispatch</h4>
      <div className="cw-dossier__people">
        <div><small>Unit</small><b>${people.assignee || "—"}</b></div>
        <div><small>Responder</small><b>${people.responder || "—"}</b></div>
        <div><small>ETA</small><b>${fmtDuration(lp.elapsed_s)} / ${fmtDuration(lp.eta_s)}</b></div>
      </div>
      ${reason ? html`<p className="cw-dossier__reason">${reason}</p>` : null}
    </section>

    <section className="cw-dossier__block">
      <h4>Field notes · ${noteRows.length}</h4>
      ${noteRows.length ? html`<ul className="cw-dossier__notes">
        ${noteRows.map((n) => html`<li key=${n.id} data-role=${n.role || "field"}
            data-on=${n.media_id && n.media_id === activeId ? "1" : "0"}
            onClick=${() => { if (n.media_id && onPickMedia) onPickMedia(n.media_id); }}>
          <b>${n.who || "Field"}</b>
          <span>${n.text}</span>
        </li>`)}
      </ul>` : html`<p className="cw-dossier__muted">No field notes.</p>`}
    </section>

    <section className="cw-dossier__block">
      <h4>AI read · fixed</h4>
      ${reads.length ? html`<ul className="cw-dossier__ai">
        ${reads.map((r) => html`<li key=${r.id} data-fixed=${r.fixed ? "1" : "0"}
            data-on=${r.id === activeId ? "1" : "0"}
            onClick=${() => onPickMedia && onPickMedia(r.id)}>
          <header>
            <b>${r.mark}</b>
            <em>${r.fixed ? "Fixed" : "Read"}</em>
          </header>
          <span>${r.read}</span>
        </li>`)}
      </ul>` : html`<p className="cw-dossier__muted">AI has not sealed media yet.</p>`}
    </section>

    <section className="cw-dossier__block">
      <h4>Place</h4>
      <p className="cw-dossier__place">${place.address || "—"}</p>
      <p className="cw-dossier__place-meta">${place.zone_id || "—"} · ${place.site_id || "—"}</p>
    </section>

    <section className="cw-dossier__block">
      <h4>Route</h4>
      ${route.length ? html`<ol className="cw-dossier__route">
        ${route.map((wp) => html`<li key=${wp.id} data-on=${wp.done ? "1" : "0"}>
          <i/>
          <div>
            <b>${wp.label}</b>
            <span>${wp.done ? "Passed" : "Next"}</span>
          </div>
        </li>`)}
      </ol>` : html`<p className="cw-dossier__muted">No waypoints.</p>`}
    </section>

    <section className="cw-dossier__block">
      <h4>Cams</h4>
      <p className="cw-dossier__cams">
        <span data-on=${camP.open ? "1" : "0"}>Person · ${camP.open ? (camP.who || "open") : "closed"}</span>
        <span data-on=${camD.open ? "1" : "0"}>Board camera · ${camD.open ? (boardHeadingOf(camD.device_id) || "open") : "closed"}</span>
      </p>
    </section>

    <section className="cw-dossier__block">
      <h4>Progress</h4>
      <div className="cw-vault__meter" aria-hidden="true"><i style=${{ width: progress + "%" }}/></div>
      <div className="cw-path cw-path--lg">
        ${stages.map((st, i) => html`<span key=${st.id} data-on=${st.done ? "1" : "0"} data-i=${i}>${st.label}</span>`)}
      </div>
    </section>

    <section className="cw-dossier__block">
      <h4>Timeline</h4>
      <ol className="cw-dossier__timeline">
        ${timeline.length ? timeline.map((node) => html`<li key=${node.id} data-k=${node.kind}
            data-on=${node.snapshot_id && node.snapshot_id === activeId ? "1" : "0"}
            onClick=${() => { if (node.snapshot_id && onPickMedia) onPickMedia(node.snapshot_id); }}>
          <time>${fmtClock(node.at)}</time>
          <div>
            <b>${node.title}</b>
            <span>${node.detail}${node.from ? ` · ${node.from}` : ""}</span>
          </div>
        </li>`) : html`<li data-k="empty"><div><span>No timeline yet.</span></div></li>`}
      </ol>
    </section>

    <section className="cw-dossier__block">
      <h4>Media · ${media.length}</h4>
      <div className="cw-dossier__media">
        ${media.length ? media.map((row) => html`<${M.button} type="button" key=${row.snapshot_id}
            data-k=${row.media_kind}
            data-fixed=${row.ai_fixed ? "1" : "0"}
            data-on=${activeId === row.snapshot_id ? "1" : "0"}
            whileHover=${HOVER} whileTap=${TAP} transition=${SOFT}
            onClick=${() => onPickMedia && onPickMedia(row.snapshot_id)}>
            ${evidenceLabel(row.media_kind)}${row.ai_fixed ? " · fixed" : ""}
          <//>`) : html`<span className="cw-dossier__muted">No media.</span>`}
      </div>
    </section>

    <section className="cw-dossier__block cw-dossier__block--end">
      <h4>Outcome</h4>
      <p className="cw-dossier__result" data-k=${closure.result}>${closure.result}</p>
      <p className="cw-dossier__after">${closure.after || "—"}</p>
    </section>
  </aside>`;
}

function ArchiveCard({ seed, index, preferred, onOpen }) {
  const archive = seed.archive || {};
  const closure = archive.closure || {};
  const people = archive.people || {};
  const place = archive.place || {};
  const kind = seed.loop ? "dispatch" : "trigger";
  const notesN = (archive.notes || []).length;
  const fixedN = archive.fixed_count || 0;

  return html`<${M.button}
      type="button"
      layout
      className="cw-acard"
      data-k=${kind}
      data-soft=${seed.soft ? "1" : "0"}
      data-preferred=${preferred ? "1" : "0"}
      data-state=${closure.state || "open"}
      initial=${{ opacity: 0, y: 14, scale: 0.98 }}
      animate=${{ opacity: 1, y: 0, scale: 1 }}
      whileHover=${{ y: -4, scale: 1.015 }}
      whileTap=${TAP}
      transition=${{ ...STAGGER, delay: Math.min(index * 0.045, 0.24) }}
      onClick=${() => onOpen && onOpen(seed.id)}>
    <header>
      <small>${kind === "dispatch" ? "Dispatch" : "Trigger"}</small>
      <em data-k=${closure.result || "open"}>${closure.state || "open"}</em>
    </header>
    <h3>${seed.id}</h3>
    <p className="cw-acard__who">${people.assignee || people.responder || seed.label || "—"}</p>
    <p className="cw-acard__place">${archive.reason || place.address || place.zone_id || seed.why || "Local trigger"}</p>
    <footer>
      <span>${seed.media_count || 0} media</span>
      ${notesN ? html`<span>${notesN} notes</span>` : null}
      ${fixedN ? html`<span data-k="fixed">${fixedN} fixed</span>` : null}
      <b>Open</b>
    </footer>
  <//>`;
}

function EvidencePage({ cardsById, localRows, preferredEventId, follow, localCount, tick }) {
  const [openId, setOpenId] = useState("");
  const [activeId, setActiveId] = useState("");
  const [filter, setFilter] = useState("all");

  const catalog = useMemo(
    () => buildEventCatalog(cardsById, localRows),
    [cardsById, localRows],
  );

  useEffect(() => {
    if (openId && !catalog.some((s) => s.id === openId)) setOpenId("");
  }, [catalog, openId]);

  useEffect(() => { setActiveId(""); setFilter("all"); }, [openId]);

  const seed = catalog.find((s) => s.id === openId) || null;
  const rows = (seed && seed.evidence) || [];
  const archive = (seed && seed.archive) || null;

  const filtered = filter === "all"
    ? rows
    : filter === "local"
      ? rows.filter((r) => r.retained_local || r.trigger_origin === "local_vault")
      : rows.filter((r) => r.media_kind === filter);
  const active = filtered.find((r) => r.snapshot_id === activeId)
    || rows.find((r) => r.snapshot_id === activeId)
    || filtered[0]
    || null;

  const uploads = rows.filter((r) => r.media_kind === "upload").length;
  const recordings = rows.filter((r) => r.media_kind === "recording").length;
  const snaps = rows.filter((r) => r.media_kind === "snapshot").length;
  const dispatches = rows.filter((r) => r.media_kind === "dispatch").length;
  const locals = rows.filter((r) => r.retained_local || r.trigger_origin === "local_vault").length;

  const setKind = (kind) => {
    setFilter((cur) => (cur === kind ? "all" : kind));
    setActiveId("");
  };

  if (!seed) {
    return html`<${M.section} className="cw-vault" aria-label="Evidence cases"
        initial=${{ opacity: 0, y: 12 }} animate=${{ opacity: 1, y: 0 }} transition=${SOFT}>
      <header className="cw-vault__head">
        <div className="cw-vault__title">
          <h2>Evidence</h2>
          <p>Closed events · local register</p>
        </div>
        <div className="cw-vault__meta">
          <b>Local</b>
          <span>${localCount}</span>
          <em>${catalog.length} cases</em>
        </div>
      </header>

      <div className="cw-acard-grid" aria-label="Case cards">
        ${catalog.length
          ? catalog.map((row, i) => html`<${ArchiveCard} key=${row.id} seed=${row} index=${i}
              preferred=${preferredEventId === row.id}
              onOpen=${(id) => setOpenId(id)}/>`)
          : html`<p className="cw-vault__empty-note">No cases yet.</p>`}
      </div>
    <//>`;
  }

  return html`<${M.section} className="cw-vault" aria-label="Evidence cases" data-open=${seed.id}
      initial=${{ opacity: 0, x: 28 }} animate=${{ opacity: 1, x: 0 }}
      transition=${{ duration: 0.34, ease: [.16, 1, .3, 1] }}>
    <header className="cw-vault__head">
      <div className="cw-vault__title">
        <${M.button} type="button" className="cw-vault__back"
            whileHover=${HOVER} whileTap=${TAP} transition=${SOFT}
            onClick=${() => setOpenId("")}>
          <${Ico} d="arrowL" w=${12}/> Cases
        <//>
        <h2>${seed.label || "Case"}</h2>
        <p className="cw-vault__case-id">${seed.id}</p>
      </div>
      <div className="cw-vault__meta">
        <span>${rows.length} media</span>
        ${(archive && archive.fixed_count) ? html`<em data-k="fixed">${archive.fixed_count} fixed</em>` : null}
      </div>
    </header>
    <${LocalEvidenceExport} className="cw-vault__export" incidentId=${seed.id} eventId=${seed.id}/>

    <div className="cw-vault__filters" role="tablist" aria-label="Evidence filters">
      ${[
        ["all", `All ${rows.length}`],
        ["snapshot", `${snaps} snaps`],
        ["recording", `${recordings} clips`],
        ["upload", `${uploads} uploads`],
        ["dispatch", `${dispatches} dispatch`],
        ["local", `${locals} local`],
      ].map(([k, label]) => html`<${M.button} type="button" role="tab" key=${k}
          data-k=${k} data-on=${filter === k ? "1" : "0"}
          whileHover=${{ scale: 1.04 }} whileTap=${TAP} transition=${SOFT}
          onClick=${() => (k === "all" ? setFilter("all") : setKind(k))}>${label}<//>`)}
    </div>

    <div className="cw-vault__body">
      <div className="cw-vault__stage">
        <${M.div} className="cw-vault__hero" data-k=${active ? active.media_kind : ""}
            key=${active ? active.snapshot_id : "empty"}
            initial=${{ opacity: 0.4, scale: 0.985 }} animate=${{ opacity: 1, scale: 1 }}
            transition=${SOFT}>
          <div className="cw-vault__frame-head">
            <small>Active evidence</small>
            <b>${active ? (active.integrity_mark || active.snapshot_id) : "No selection"}</b>
          </div>
          <${EvidenceHero} active=${active}/>
          ${active && active.media_kind !== "dispatch" ? html`<div className="cw-vault__caption">
            <div>
              <b>${evidenceLabel(active.media_kind)}${active.ai_fixed ? " · fixed" : ""}</b>
              ${active.note ? html`<p>${active.note}</p>` : null}
              ${active.ai_read ? html`<em>${active.ai_read}</em>` : null}
            </div>
            <span>${active.author || active.from_label || "—"}</span>
          </div>` : null}
        <//>
        <div className="cw-vault__grid" aria-label="Evidence library grid">
          ${filtered.length ? filtered.map((row, i) => html`<${M.button} type="button"
              key=${row.snapshot_id + ":" + row.media_kind}
              className="cw-vault__tile"
              data-k=${row.media_kind}
              data-local=${row.retained_local ? "1" : "0"}
              data-fixed=${row.ai_fixed ? "1" : "0"}
              data-on=${(active && active.snapshot_id === row.snapshot_id) ? "1" : "0"}
              initial=${{ opacity: 0, y: 10 }}
              animate=${{ opacity: 1, y: 0 }}
              whileHover=${{ y: -4, scale: 1.03 }}
              whileTap=${TAP}
              transition=${{ ...STAGGER, delay: Math.min(i * 0.035, 0.18) }}
              onClick=${() => setActiveId(row.snapshot_id)}
              title=${row.note || row.detail || row.integrity_mark || ""}>
              ${row.media_kind === "dispatch"
                ? html`<span className="cw-vault__tile-dsp">Dispatch</span>`
                : row.image_url
                  ? html`<img className="cw-vault__blob" src=${row.image_url}
                      alt=${row.integrity_mark || "retained evidence"}/>`
                : html`<${SoftPhosphor} tone=${row.soft_frame_tone || "dock"} stamp=${row.integrity_mark || "EV"} quiet=${true}
                    kindMark=${evidenceLabel(row.media_kind)}
                    mediaKind=${row.media_kind === "audit" ? "snapshot" : row.media_kind}/>`}
              <em>${evidenceLabel(row.media_kind)}${row.ai_fixed ? " · fixed" : ""}</em>
            <//>`) : html`<p className="cw-vault__empty-note">${rows.length ? "Nothing in this filter." : "No media in this case."}</p>`}
        </div>
      </div>

      <${EventDossier}
        archive=${archive}
        activeId=${active && active.snapshot_id}
        onPickMedia=${(id) => setActiveId(id)}
      />
    </div>
  <//>`;
}


export function CamerasLiveWall({ toast }) {
  const udockPosture = useApi(path("get__udock_status"));
  const liveOk = udockPosture.loading ? false : (
    !!(udockPosture.data && udockPosture.data.write_allowed === true)
  );
  const wall = usePolled("/cameras/wall", 15000);
  const signalsLive = useCameraSignalsLive(12000);
  const liveSite = useLive() || {};
  const merged = useMemo(() => mergeSoftCinemaDemo(wall.data), [wall.data]);
  const softDemo = !!(merged && merged.soft_demo);
  const deviceCards = useMemo(() => merged.device_cards || [], [merged]);
  const dispatchCardsRaw = useMemo(() => merged.dispatch_cards || [], [merged]);
  const allVideoCards = deviceCards;
  const allDispatchedCards = useMemo(
    () => buildDispatchBoard(deviceCards, dispatchCardsRaw),
    [deviceCards, dispatchCardsRaw],
  );

  const cardsById = useMemo(() => {
    const map = {};
    for (const c of allVideoCards) map[c.source_id] = c;
    for (const c of allDispatchedCards) map[c.source_id] = c;
    for (const c of dispatchCardsRaw) map[c.source_id] = c;
    return map;
  }, [allVideoCards, allDispatchedCards, dispatchCardsRaw]);

  const [selectedId, setSelectedId] = useState("");
  const [inspectId, setInspectId] = useState("");
  const [barOpen, setBarOpen] = useState(true);
  const [page, setPage] = useState("wall");
  const [scope, setScope] = useState({ mode: "all", id: "" });
  const [tickets, setTickets] = useState({});
  const [localRows, setLocalRows] = useState([]);
  const [liveBusyId, setLiveBusyId] = useState("");
  const [tick, setTick] = useState(0);
  const handoffDone = useRef(false);
  const retainedSignalIds = useRef(new Set());

  const openEvidence = () => setPage("vault");
  const openWall = () => setPage("wall");

  const deviceMap = useMemo(
    () => new Map((liveSite.devices || []).map((row) => [String(row.id || ""), row])),
    [liveSite.devices],
  );
  const scopeOptions = useMemo(
    () => buildScopeOptions(allVideoCards, deviceMap, liveSite.zones || []),
    [allVideoCards, deviceMap, liveSite.zones],
  );

  const videoCards = useMemo(
    () => allVideoCards.filter((card) => scopeMatches(card, scope, deviceMap, cardsById)),
    [allVideoCards, scope.mode, scope.id, deviceMap, cardsById],
  );
  const cinemaLiveN = useMemo(
    () => videoCards.filter((card) => liveOf(card)).length,
    [videoCards],
  );
  const dispatchedCards = useMemo(
    () => allDispatchedCards.filter((card) => scopeMatches(card, scope, deviceMap, cardsById)),
    [allDispatchedCards, scope.mode, scope.id, deviceMap, cardsById],
  );

  const selected = selectedId ? cardsById[selectedId] || null : null;
  const inspectCard = inspectId ? cardsById[inspectId] || null : null;
  const openInspect = (id) => {
    setSelectedId(id);
    setInspectId(id);
  };
  const selectedScope = selected ? scopeOf(selected, deviceMap) : { device: "", zone: "", group: "" };
  const selectedLoop = selected ? (loopIdOf(selected) || selected.loop_key || "") : "";
  const relatedIds = useMemo(() => {
    const set = new Set();
    if (!selectedLoop) return set;
    for (const c of allVideoCards) {
      if (loopIdOf(c) === selectedLoop) set.add(c.source_id);
    }
    for (const c of allDispatchedCards) {
      if ((c.loop_key || loopIdOf(c)) === selectedLoop) {
        set.add(c.source_id);
        for (const id of c.linked_source_ids || []) set.add(id);
      }
    }
    return set;
  }, [selectedLoop, allVideoCards, allDispatchedCards]);

  const alarmDevices = useMemo(
    () => new Set((liveSite.alarms || []).filter((row) => row && row.open !== false).map((row) => String(row.dev || ""))),
    [liveSite.alarms],
  );
  const alarmLinkedIds = useMemo(() => {
    const out = new Set();
    if (!selected || (!selectedScope.zone && !selectedScope.group)) return out;
    for (const card of allVideoCards) {
      const info = scopeOf(card, deviceMap);
      const shares = (selectedScope.zone && info.zone === selectedScope.zone)
        || (selectedScope.group && info.group === selectedScope.group);
      if (shares && alarmDevices.has(info.device)) out.add(card.source_id);
    }
    return out;
  }, [selected, selectedScope.zone, selectedScope.group, allVideoCards, deviceMap, alarmDevices]);

  const allSignals = useMemo(
    () => buildCameraSignalFeed(
      merged.activity || [],
      (signalsLive.data && signalsLive.data.items) || [],
      [...allVideoCards, ...allDispatchedCards, ...dispatchCardsRaw],
    ),
    [merged.activity, signalsLive.data, allVideoCards, allDispatchedCards, dispatchCardsRaw],
  );
  const signals = useMemo(() => allSignals.filter((signal) => {
    if (scope.mode === "all" || !scope.id) return true;
    const direct = cardsById[signal.source_id];
    if (direct) return scopeMatches(direct, scope, deviceMap, cardsById);
    const card = allVideoCards.find((row) => deviceIdOf(row) === signal.source_id);
    return card ? scopeMatches(card, scope, deviceMap, cardsById) : false;
  }), [allSignals, scope.mode, scope.id, cardsById, deviceMap, allVideoCards]);
  const inspectSignals = useMemo(() => {
    if (!inspectCard) return [];
    const sid = String(inspectCard.source_id || "");
    const device = deviceIdOf(inspectCard);
    return allSignals.filter((row) => {
      const src = String(row.source_id || "");
      return src === sid || (device && src === device);
    });
  }, [inspectCard, allSignals]);

  const follow = useMemo(() => aiFollowOf(selected, tick), [selected, tick]);
  const inspectFollow = useMemo(() => aiFollowOf(inspectCard, tick), [inspectCard, tick]);
  const loop = useMemo(() => (selected ? dispatchLoopOf(sitOf(selected)) : null), [selected]);
  const preferredEventId = useMemo(
    () => (selected ? eventIdOf(selected) : ""),
    [selected],
  );

  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1600);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (selectedId && !cardsById[selectedId]) setSelectedId("");
    if (inspectId && !cardsById[inspectId]) setInspectId("");
  }, [selectedId, inspectId, cardsById]);

  useEffect(() => {
    if (handoffDone.current || !allVideoCards.length) return;
    const handoff = readCameraHandoff();
    const hit = allVideoCards.find((card) => {
      const info = scopeOf(card, deviceMap);
      return (handoff.source && card.source_id === handoff.source)
        || (handoff.device && info.device === handoff.device)
        || (handoff.zone && info.zone === handoff.zone)
        || (handoff.group && info.group === handoff.group);
    });
    if (hit) setSelectedId(hit.source_id);
    if (handoff.device) setScope({ mode: "device", id: handoff.device });
    else if (handoff.zone) setScope({ mode: "zone", id: handoff.zone });
    else if (handoff.group) setScope({ mode: "group", id: handoff.group });
    handoffDone.current = true;
  }, [allVideoCards, deviceMap]);

  useEffect(() => {
    if (!selectedId && videoCards.length) setSelectedId(videoCards[0].source_id);
  }, [videoCards.map((c) => c.source_id).join("|"), selectedId]);

  useEffect(() => {
    if (selectedId && !videoCards.some((card) => card.source_id === selectedId) && videoCards.length) {
      setSelectedId(videoCards[0].source_id);
    }
  }, [scope.mode, scope.id, selectedId, videoCards.map((c) => c.source_id).join("|")]);

  useEffect(() => {
    let dead = false;
    const preferred = [];
    const pushLive = (id) => {
      const card = id ? cardsById[id] : null;
      if (card && liveOf(card) && !preferred.includes(card.source_id)) preferred.push(card.source_id);
    };
    pushLive(inspectId);
    pushLive(selectedId);
    for (const card of videoCards) {
      if (preferred.length >= 6) break;
      if (liveOf(card) && !preferred.includes(card.source_id)) preferred.push(card.source_id);
    }
    if (!preferred.length) { setTickets({}); return undefined; }
    (async () => {
      try {
        const res = await api("/map/preview-subscriptions", {
          method: "POST",
          body: { source_ids: preferred, max_streams: Math.min(6, preferred.length) },
        });
        if (dead) return;
        const by = {};
        for (const row of (res && res.items) || []) {
          if (row && row.source_id) by[String(row.source_id)] = row;
        }
        setTickets(by);
      } catch {
        if (!dead) setTickets({});
      }
    })();
    return () => { dead = true; };
  }, [
    inspectId,
    selectedId,
    videoCards.map((c) => c.source_id + ":" + liveOf(c)).join("|"),
    Object.keys(cardsById).join("|"),
  ]);

  const refreshLocal = async () => {
    try {
      setLocalRows(await listLocalEvidence({ limit: 80 }));
    } catch {
      setLocalRows([]);
    }
  };

  useEffect(() => { refreshLocal(); }, [selectedId, tick]);

  useEffect(() => {
    let dead = false;
    (async () => {
      let wrote = false;
      for (const signal of signals.slice(0, 40)) {
        const stableId = "sig_" + String(signal.id || "").replace(/[^a-zA-Z0-9_.:-]/g, "_").slice(0, 120);
        if (!stableId || retainedSignalIds.current.has(stableId)) continue;
        retainedSignalIds.current.add(stableId);
        const card = cardsById[signal.source_id]
          || allVideoCards.find((row) => deviceIdOf(row) === signal.source_id)
          || selected;
        const eid = card ? eventIdOf(card) : (signal.source_id ? `trig:${signal.source_id}` : "signal:unbound");
        const lid = card ? loopIdOf(card) : "";
        await saveAuditLocal({
          id: stableId,
          sourceId: (card && card.source_id) || signal.source_id,
          label: cameraLabelSay((card && card.label) || signal.title),
          detail: `${signal.title} · ${signal.detail}`,
          triggerKind: softDemo ? "soft" : (signal.kind === "ai" ? "ai" : "system"),
          eventId: eid,
          loopId: lid,
          at: signal.at,
          author: signal.control || "event stream",
          authorRole: signal.kind,
          aiRead: `Observed via ${signalsLive.transport === "sse" ? "SSE" : "poll"}; source preserved.`,
          aiFixed: signal.kind === "action",
        });
        wrote = true;
      }
      if (wrote && !dead) await refreshLocal();
    })().catch(() => {});
    return () => { dead = true; };
  }, [signals.map((row) => row.id).join("|"), cardsById, selected && selected.source_id, softDemo, signalsLive.transport]);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        if (selected) {
          const sit = sitOf(selected);
          const eid = eventIdOf(selected);
          const lid = (loop && loop.id) || "";
          const rows = Array.isArray(sit.evidence) ? sit.evidence : [];
          if (rows.length) {
            await retainMediaEvidenceLocal(rows, {
              sourceId: selected.source_id,
              fromLabel: selected.label || selected.source_id,
              eventId: eid,
              loopId: lid,
            });
          }
          const linked = selected.linked_source_ids || [];
          for (const id of linked) {
            const other = cardsById[id];
            if (!other) continue;
            if (eventIdOf(other) !== eid) continue;
            const er = Array.isArray(sitOf(other).evidence) ? sitOf(other).evidence : [];
            if (er.length) {
              await retainMediaEvidenceLocal(er, {
                sourceId: other.source_id,
                fromLabel: other.label || id,
                eventId: eid,
                loopId: lid,
              });
            }
          }
        }
        if (loop && loop.id) {
          await saveDispatchLocal({
            loop,
            sourceId: selected && selected.source_id,
            label: (selected && selected.label) || loop.assignee || loop.id,
          });
        }
        if (!dead) await refreshLocal();
      } catch { /* local retain best-effort */ }
    })();
    return () => { dead = true; };
  }, [selected && selected.source_id, loop && loop.id, (selected && sitOf(selected).evidence || []).map((r) => r.snapshot_id).join("|")]);

  const onToggleLive = async (card) => {
    if (!liveOk) {
      toast && toast(
        (udockPosture.data && udockPosture.data.armed === true)
          ? "Your account cannot open live video on this unit."
          : "Live video is not armed on this site yet.",
        "warn",
      );
      return;
    }
    const sourceId = String(card && card.source_id || "");
    const did = deviceIdOf(card);
    const sid = sessionIdOf(card);
    if (!did || liveBusyId) return;
    setLiveBusyId(sourceId);
    try {
      const result = sid
        ? await closeLiveSession(did, sid, "Manual Live close from Cameras wall")
        : await openLiveSession(did, {
            camera_id: cameraIdOf(card),
            operator_reason: "Manual Live open from Cameras wall",
            ttl_s: 300,
            trigger_kind: "manual",
          });
      if (!result || result.ok !== true) {
        throw new Error((result && result.error) || "Live command failed");
      }
      toast && toast(sid ? "Live closed · resources released" : "Live opening · picture follows media", "go");
      try { await api("/cameras/wall?fresh=1"); } catch { /* replica stamp bust is enough */ }
      wall.reload && wall.reload();
    } catch (err) {
      toast && toast(String((err && err.message) || err || "Live command failed"), "err");
    } finally {
      setLiveBusyId("");
    }
  };

  /* Multi-cam: open (or close) every eligible lens at once, capped by the console seat quota.
     Parallel, not one-by-one; each result is honest — refused seats stay dark. */
  const bulkTargets = useMemo(() => {
    const open = [];
    const close = [];
    for (const card of videoCards) {
      if (!card || card.soft_stamp || card.soft_demo || card.soft_neq_live) continue;
      const did = deviceIdOf(card);
      if (!did) continue;
      if (sessionIdOf(card)) close.push(card);
      else open.push(card);
    }
    return { open: open.slice(0, LIVE_CONSOLE_CAP), close };
  }, [videoCards]);
  const [bulkBusy, setBulkBusy] = useState("");
  const onBulkLive = async (mode) => {
    if (!liveOk) {
      toast && toast(
        (udockPosture.data && udockPosture.data.armed === true)
          ? "Your account cannot open live video on this unit."
          : "Live video is not armed on this site yet.",
        "warn",
      );
      return;
    }
    const list = mode === "close" ? bulkTargets.close : bulkTargets.open;
    if (!list.length || bulkBusy) return;
    setBulkBusy(mode);
    try {
      const results = await Promise.all(list.map(async (card) => {
        const did = deviceIdOf(card);
        const sid = sessionIdOf(card);
        try {
          const r = mode === "close"
            ? await closeLiveSession(did, sid, "Stop all Live from Cameras wall")
            : await openLiveSession(did, {
                camera_id: cameraIdOf(card),
                operator_reason: "Start all Live from Cameras wall",
                ttl_s: 300,
                trigger_kind: "manual",
              });
          return !!(r && r.ok === true);
        } catch {
          return false;
        }
      }));
      const ok = results.filter(Boolean).length;
      toast && toast(
        (mode === "close" ? "Live closed · " : "Live opening · ") + ok + "/" + list.length,
        ok ? "go" : "warn",
      );
      try { await api("/cameras/wall?fresh=1"); } catch { /* */ }
      wall.reload && wall.reload();
    } finally {
      setBulkBusy("");
    }
  };

  const onSaveFrame = async (card, video) => {
    const eid = eventIdOf(card);
    const lid = loopIdOf(card) || "";
    const did = deviceIdOf(card);
    const camId = cameraIdOf(card);
    const sid = sessionIdOf(card);
    const live = liveOf(card) && video && video.videoWidth > 0;
    if (live && did && !liveOk) {
      toast && toast(
        (udockPosture.data && udockPosture.data.armed === true)
          ? "Your account cannot save Live frame evidence on this unit."
          : "Live video is not armed on this site yet.",
        "warn",
      );
      return;
    }
    if (live && did) {
      try {
        const { local, server } = await captureFrameToServer({
          video,
          apiPost: (p, body) => api(p, { method: "POST", body }),
          deviceId: did,
          cameraId: camId,
          sourceId: card.source_id,
          sessionId: sid,
          label: card.label,
          triggerKind: "human",
          eventId: eid,
          loopId: lid,
        });
        const snap = server && server.snapshot_id ? String(server.snapshot_id) : "";
        toast && toast(
          snap
            ? `Live frame evidence · ${snap}`
            : `Live frame saved · ${local.integrity_mark}`,
          "go",
        );
      } catch (e) {
        const msg = String((e && e.message) || e || "");
        if (msg.includes("live_session") || msg.includes("live_media_not_played")) {
          toast && toast("No Live frame — open Live first", "warn");
        } else {
          toast && toast(msg || "Live frame ingest failed", "err");
        }
      }
    } else if (video && video.videoWidth) {
      // Soft path only when Live envelope is not authoritative — local only.
      const saved = await captureFrameToLocal({
        video, sourceId: card.source_id, label: card.label, triggerKind: "human",
        eventId: eid, loopId: lid,
      });
      toast && toast("Local only · not Live evidence · " + saved.integrity_mark, "warn");
    } else {
      await saveAuditLocal({
        sourceId: card.source_id,
        label: card.label,
        detail: softDemo ? "Soft demo audit — no Live frame" : "No Live frame — evidence refused",
        triggerKind: "human",
        eventId: eid,
        loopId: lid,
      });
      toast && toast(softDemo ? "Soft audit saved locally" : "No Live frame — nothing ingested", "warn");
    }
    await refreshLocal();
  };

  const scopeLabel = scope.mode === "all"
    ? "All cameras"
    : `${scope.mode[0].toUpperCase() + scope.mode.slice(1)} · ${looksLikeCode(scope.id) ? "this place" : (scope.id || "unavailable")}`;

  return html`<div className="cw-tri"
      data-cmc-cam-tri="1"
      data-soft-demo=${softDemo ? "1" : "0"}
      data-bar=${barOpen ? "1" : "0"}
      data-page=${page}
      data-selected=${selected ? "1" : "0"}>
    <div className="cw-void" aria-hidden="true"><i/><i/><i/></div>

    ${!barOpen ? html`<${M.button} type="button" className="cw-bar-reveal"
        initial=${{ opacity: 0, y: -8 }} animate=${{ opacity: 1, y: 0 }}
        whileHover=${{ scale: 1.04, y: 1 }} whileTap=${TAP} transition=${SOFT}
        onClick=${() => setBarOpen(true)}>
      Show the bar
    <//>` : null}

    <div className="cw-tri__shell">
      <${AnimatePresence}>
        ${barOpen ? html`<${M.header} className="cw-top glass" role="banner" key="bar" data-chrome="v2"
            initial=${{ y: -18, opacity: 0 }} animate=${{ y: 0, opacity: 1 }}
            exit=${{ y: -12, opacity: 0 }} transition=${{ duration: 0.28, ease: [.16, 1, .3, 1] }}>
          <div className="cw-top__lead">
            <${M.button} type="button" className="cw-top__exit"
                whileHover=${HOVER} whileTap=${TAP} transition=${SOFT}
                onClick=${() => go("/ops")}>
              <${Ico} d="arrowL" w=${14}/> Ops
            <//>
            <div className="cw-top__brand" aria-hidden="true">
              <b>CAMERAS</b>
              <small data-live=${cinemaLiveN > 0 ? "1" : "0"}>${cinemaLiveN > 0 ? "Live" : "Standby"}</small>
            </div>
          </div>
          <div className="cw-top__pages" role="tablist" aria-label="Camera pages">
            <${M.button} type="button" role="tab" data-on=${page === "wall" ? "1" : "0"}
              aria-selected=${page === "wall" ? "true" : "false"}
              whileHover=${{ scale: 1.02 }} whileTap=${TAP} transition=${SOFT}
              onClick=${openWall}>Live<//>
            <${M.button} type="button" role="tab" data-on=${page === "vault" ? "1" : "0"}
              aria-selected=${page === "vault" ? "true" : "false"}
              whileHover=${{ scale: 1.02 }} whileTap=${TAP} transition=${SOFT}
              onClick=${openEvidence}>Evidence · ${localRows.length}<//>
          </div>
          <div className="cw-top__status">
            <${AiWitnessMark} state=${follow.state}/>
          </div>
          <div className="cw-top__acts">
            <${M.button} type="button" className="btn btn--ghost btn--xs cw-top__action cw-top__action--refresh" disabled=${wall.loading}
              whileHover=${{ scale: 1.05 }} whileTap=${TAP} transition=${SOFT}
              onClick=${() => { wall.reload && wall.reload(); toast && toast("Cameras updated", "go"); }}>
              <${Ico} d="refresh" w=${12}/> Refresh
            <//>
            <${M.button} type="button" className="btn btn--ghost btn--xs cw-top__action"
              whileHover=${{ scale: 1.05 }} whileTap=${TAP} transition=${SOFT}
              onClick=${() => setBarOpen(false)}>
              Hide bar
            <//>
          </div>
        <//>` : null}
      <//>

      ${wall.error && !softDemo
        ? html`<${Alert} tone="warn" title="Camera wall unavailable" say=${wall.error}/>`
        : null}
      ${!wall.error && wall.data && wall.data.honesty === "registry_budget_hold"
        ? html`<${Alert} tone="warn" title="Cameras are busy"
            say="Too many cameras to read at once. Tap Refresh and try again."/>`
        : null}

      <${AnimatePresence} mode="wait">
        ${page === "vault"
          ? html`<${M.div} key="vault" className="cw-tri__page" initial=${{ opacity: 0, x: 36 }}
              animate=${{ opacity: 1, x: 0 }} exit=${{ opacity: 0, x: -24 }}
              transition=${{ duration: 0.34, ease: [.16, 1, .3, 1] }}>
            <${EvidencePage}
              cardsById=${cardsById}
              localRows=${localRows}
              preferredEventId=${preferredEventId}
              follow=${follow}
              localCount=${localRows.length}
              tick=${tick}
            />
          <//>`
          : html`<${M.div} key="wall" className="cw-tri__page" initial=${{ opacity: 0, x: -28 }}
              animate=${{ opacity: 1, x: 0 }} exit=${{ opacity: 0, x: 28 }}
              transition=${{ duration: 0.34, ease: [.16, 1, .3, 1] }}>
            <div className="cw-tri__body" data-page="wall">
              <${CameraSignalRail}
                signals=${signals}
                transport=${signalsLive.transport}
                scopeLabel=${scopeLabel}
                selectedSourceId=${selected && selected.source_id}
              />
              <div className="cw-tri__work">
                <${ScopeSelector}
                  scope=${scope}
                  setScope=${setScope}
                  options=${scopeOptions}
                  selectedScope=${selectedScope}
                />
          <${LayoutGroup}>
            <section className="cw-tri__video" aria-label="Video cards">
              <div className="cw-zone__hd">
                <div>
                  <h3>Lenses</h3>
                  <p>${scopeLabel}</p>
                </div>
                <div className="btn-row cw-zone__bulk" data-cmc-bulk-live="1">
                  ${bulkTargets.open.length ? html`<button type="button" className="btn btn--ghost btn--xs"
                    data-cmc-cam-live-ok=${liveOk ? "1" : "0"}
                    disabled=${!!bulkBusy || !liveOk}
                    title=${"Open up to " + LIVE_CONSOLE_CAP + " lenses at once"}
                    onClick=${() => onBulkLive("open")}>
                    ${bulkBusy === "open" ? "Opening…" : "Start all · " + bulkTargets.open.length}</button>` : null}
                  ${bulkTargets.close.length ? html`<button type="button" className="btn btn--ghost btn--xs"
                    disabled=${!!bulkBusy || !liveOk}
                    onClick=${() => onBulkLive("close")}>
                    ${bulkBusy === "close" ? "Closing…" : "Stop all · " + bulkTargets.close.length}</button>` : null}
                  <em>${videoCards.length}</em>
                </div>
              </div>
              <div className="cw-tri__video-grid">
                <${AnimatePresence} mode="popLayout">
                  ${videoCards.length ? videoCards.map((card, i) => {
                    const ticket = tickets[card.source_id] || {};
                    return html`<${VideoCard}
                      key=${card.source_id}
                      card=${card}
                      index=${i}
                      selected=${selectedId === card.source_id || inspectId === card.source_id}
                      related=${relatedIds.has(card.source_id)}
                      alarmLinked=${alarmLinkedIds.has(card.source_id)}
                      whepUrl=${String(ticket.whep_ticket_url || "")}
                      ticketReason=${ticket.reason || ""}
                      onSelect=${openInspect}
                      onToggleLive=${onToggleLive}
                      liveBusy=${liveBusyId === card.source_id}
                      liveOk=${liveOk}
                      onSaveFrame=${onSaveFrame}
                      onOpenEvidence=${openEvidence}
                      toast=${toast}
                    />`;
                  }) : html`<${M.div} key="empty-v" className="cw-empty"
                    initial=${{ opacity: 0 }} animate=${{ opacity: 1 }} transition=${SOFT}>
                    <b>No lenses yet</b>
                    <span>Device previews land here as glass cards.</span>
                  <//>`}
                <//>
              </div>
            </section>

            <section className="cw-tri__dispatched" aria-label="Dispatched cards">
              <div className="cw-zone__hd">
                <div>
                  <h3>Dispatched</h3>
                  <p>${dispatchedCards.length
                    ? "Tap a card to watch"
                    : "Nobody is being sent here yet"}</p>
                </div>
                <em>${dispatchedCards.length}</em>
              </div>
              <div className="cw-tri__disp-grid">
                <${AnimatePresence} mode="popLayout">
                  ${dispatchedCards.length ? dispatchedCards.map((card, i) => html`<${DispatchCard}
                    key=${card.source_id}
                    card=${card}
                    index=${i}
                    selected=${selectedId === card.source_id || inspectId === card.source_id}
                    related=${relatedIds.has(card.source_id) || (selectedLoop && card.loop_key === selectedLoop)}
                    onSelect=${openInspect}
                  />`) : html`<${M.div} key="empty-d" className="cw-empty cw-empty--sm"
                    initial=${{ opacity: 0 }} animate=${{ opacity: 1 }} transition=${SOFT}>
                    <b>Nobody on the way</b>
                    <span>When someone is sent, their camera and the live picture show up here.</span>
                  <//>`}
                <//>
              </div>
            </section>
          <//>
              </div>
            </div>
            <${AnimatePresence}>
              ${inspectCard ? html`<${LiveLensSheet}
                key=${"sheet-" + inspectCard.source_id}
                card=${inspectCard}
                signals=${inspectSignals}
                follow=${inspectFollow}
                whepUrl=${String((tickets[inspectCard.source_id] || {}).whep_ticket_url || "")}
                ticketReason=${(tickets[inspectCard.source_id] || {}).reason || ""}
                onClose=${() => setInspectId("")}
                onOpenEvidence=${openEvidence}
                onFocusMap=${focusMap}
              />` : null}
            <//>
          <//>`}
      <//>

    </div>
  </div>`;
}

export default CamerasLiveWall;
